import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  HStack,
  Text,
  Badge,
  Spinner,
  Alert,
  VStack,
  useDisclosure,
  NativeSelect,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi'
import { CanvassingPageShell } from '../components/canvassing/CanvassingPageShell'
import { TagFormModal } from '../components/tags/TagFormModal'
import { DeleteTagModal } from '../components/tags/DeleteTagModal'
import { useTags, useDeleteTag } from '../hooks/useTag'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import type { TagDetailDto, TagSummaryDto } from '../types/tag'
import { TAG_TYPES } from '../types/tag'

export function CanvassingTagsPage() {
  const { data: tags, isLoading, error } = useTags()
  const { open, onOpen, onClose } = useDisclosure()
  const {
    open: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()
  const [selectedTag, setSelectedTag] = useState<TagDetailDto | null>(null)
  const [tagToDelete, setTagToDelete] = useState<TagSummaryDto | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const deleteMutation = useDeleteTag()

  const filteredTags = useMemo(() => {
    if (!tags) return []
    return tags.filter(tag => {
      const matchesSearch =
        !searchTerm ||
        tag.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tag.labelSpanish ?? '').toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType = !typeFilter || tag.tagType === typeFilter
      return matchesSearch && matchesType
    })
  }, [tags, searchTerm, typeFilter])

  const handleCreate = () => {
    setSelectedTag(null)
    onOpen()
  }

  const handleEdit = (tag: TagDetailDto) => {
    setSelectedTag(tag)
    onOpen()
  }

  const handleClose = () => {
    setSelectedTag(null)
    onClose()
  }

  const handleDeleteClick = (tag: TagSummaryDto) => {
    setTagToDelete(tag)
    onDeleteOpen()
  }

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return
    try {
      await deleteMutation.mutateAsync(tagToDelete.id)
      toaster.create({ title: 'Tag deleted', type: 'success', duration: 2000 })
      onDeleteClose()
      setTagToDelete(null)
    } catch {
      toaster.create({ title: 'Failed to delete tag', type: 'error', duration: 3000 })
    }
  }

  const columns: ResponsiveColumn<TagSummaryDto>[] = [
    {
      header: 'Label',
      mobileRole: 'title',
      cell: (tag) => (
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">{tag.label}</Text>
          {tag.labelSpanish && (
            <Text fontSize="xs" color="gray.500">
              {tag.labelSpanish}
            </Text>
          )}
        </VStack>
      ),
    },
    {
      header: 'Tag Type',
      mobileRole: 'badge',
      cell: (tag) =>
        tag.tagType ? (
          <Badge bg="info.subtle" color="info.fg" fontSize="xs">
            {tag.tagType}
          </Badge>
        ) : (
          <Text fontSize="sm" color="gray.400">--</Text>
        ),
    },
    {
      header: 'Visible',
      mobileRole: 'meta',
      cell: (tag) => (
        <Badge colorPalette={tag.isVisible ? 'green' : 'gray'} fontSize="xs">
          {tag.isVisible ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      header: 'Assignable',
      mobileRole: 'meta',
      cell: (tag) => (
        <Badge colorPalette={tag.canBeAssigned ? 'green' : 'gray'} fontSize="xs">
          {tag.canBeAssigned ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      header: 'Contacts',
      mobileRole: 'meta',
      cell: (tag) => <Text fontSize="sm">{tag.contactCount}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (tag) => (
        <Badge colorPalette={tag.isActive ? 'green' : 'red'} fontSize="xs">
          {tag.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      header: 'Created',
      mobileRole: 'meta',
      cell: (tag) => (
        <VStack align="start" gap={0}>
          <Text fontSize="sm">
            {new Date(tag.createdAt).toLocaleDateString()}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {tag.createdByName}
          </Text>
        </VStack>
      ),
    },
  ]

  return (
    <CanvassingPageShell
      title="Tags"
      subtitle="Define and manage tags that canvassers can apply to contacts"
    >
      <VStack gap={6} align="stretch">
        <HStack justify="flex-end">
          <Button colorPalette="brand" size="sm" onClick={handleCreate}><FiPlus />Create Tag
                      </Button>
        </HStack>

        <FilterBar
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: 'Search tags...',
          }}
          filterCount={typeFilter !== '' ? 1 : 0}
        >
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              maxW={{ base: 'full', md: '200px' }}
              placeholder="All Types"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}>
              {TAG_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </FilterBar>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading tags...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load tags. Please try again.
          </Alert.Root>
        )}

        {!isLoading && tags && tags.length === 0 && (
          <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500" mb={4}>No tags found</Text>
            <Button colorPalette="brand" size="sm" onClick={handleCreate}><FiPlus />Create Your First Tag
                          </Button>
          </Box>
        )}

        {!isLoading && tags && tags.length > 0 && filteredTags.length === 0 && (
          <Box bg="surface" p={8} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500">No tags match your search criteria.</Text>
          </Box>
        )}

        {!isLoading && filteredTags.length > 0 && (
          <ResponsiveTable
            columns={columns}
            rows={filteredTags}
            keyFn={(tag) => tag.id}
            rowActions={[
              {
                label: 'Edit',
                icon: FiEdit2,
                onClick: (tag) => handleEdit(tag as unknown as TagDetailDto),
              },
              {
                label: 'Delete',
                icon: FiTrash2,
                color: 'red.500',
                onClick: (tag) => handleDeleteClick(tag),
              },
            ]}
          />
        )}
      </VStack>

      <TagFormModal isOpen={open} onClose={handleClose} tag={selectedTag} />

      <DeleteTagModal
        isOpen={isDeleteOpen}
        onClose={() => { onDeleteClose(); setTagToDelete(null) }}
        tagLabel={tagToDelete?.label ?? ''}
        contactCount={tagToDelete?.contactCount ?? 0}
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </CanvassingPageShell>
  );
}
